import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowDownRight, ArrowUpRight, Plus, X, Scale } from "lucide-react";

const TYPE_LABEL = {
  opening: "Opening",
  receive: "Receive",
  transfer_in: "Transfer in",
  transfer_out: "Transfer out",
  sale: "Sale",
  wastage: "Wastage",
  adjustment: "Adjustment",
  count_correction: "Count correction",
};

const INCREASES = new Set(["opening", "receive", "transfer_in"]);

export default function InventoryMovementsPage() {
  const { branchId, hasPerm } = useAuth();
  const [movements, setMovements] = useState([]);
  const [items, setItems] = useState([]);
  const [type, setType] = useState("all");
  const [itemFilter, setItemFilter] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!branchId) return;
    try {
      const params = { branch_id: branchId, limit: 200 };
      if (type !== "all") params.type = type;
      if (itemFilter) params.item_id = itemFilter;
      const [m, i] = await Promise.all([
        api.get("/inventory/movements", { params }),
        items.length ? Promise.resolve({ data: items }) : api.get("/inventory/items"),
      ]);
      setMovements(m.data);
      if (!items.length) setItems(i.data);
    } catch (_) {}
    finally { setLoading(false); }
  };
  useEffect(() => { setLoading(true); load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [branchId, type, itemFilter]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/inventory/movements", {
        branch_id: branchId,
        item_id: form.item_id,
        type: form.type,
        quantity: parseFloat(form.quantity),
        as_purchase_unit: form.as_purchase_unit,
        unit_cost: form.unit_cost ? parseFloat(form.unit_cost) : null,
        reason: form.reason,
        notes: form.notes,
      });
      toast.success(`${TYPE_LABEL[form.type]} recorded`);
      setAdding(false); setForm(BLANK); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const item = useMemo(() => items.find((i) => i.id === form.item_id), [items, form.item_id]);

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="inventory-movements-page">
      <div className="flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="section-eyebrow ink-subtle">Inventory · Append-only ledger</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">Stock movements</h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">
            Every quantity change lives here — receives, wastage, transfers, adjustments and end-of-day count corrections. Nothing is edited or deleted; corrections are new entries.
          </p>
        </div>
        {hasPerm("inventory.adjust") && (
          <button onClick={() => setAdding(true)} data-testid="inv-mv-new" className="btn-pill btn-pill-solid h-10">
            <Plus size={14}/> Record movement
          </button>
        )}
      </div>

      {!branchId && (
        <div className="mt-14 border hairline-soft rounded-md p-8 text-center ink-subtle text-sm">
          Select a branch first.
        </div>
      )}

      {branchId && (
        <>
          <div className="mt-10 flex flex-wrap items-center gap-2 animate-fade-up animate-fade-up-1">
            {["all","receive","wastage","adjustment","count_correction","transfer_in","transfer_out","opening"].map((t) => (
              <button key={t} onClick={() => setType(t)}
                      data-testid={`inv-mv-tab-${t}`}
                      className={`h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.24em] font-bold border transition-colors ${
                        type === t ? "bg-foreground text-background border-foreground" : "hairline text-foreground/60 hover:text-foreground"
                      }`}>
                {TYPE_LABEL[t] || t}
              </button>
            ))}
            <select value={itemFilter} onChange={(e) => setItemFilter(e.target.value)}
                    data-testid="inv-mv-item-filter"
                    className="h-8 rounded-full border hairline surface-card px-3 text-[11px] uppercase tracking-[0.2em] font-bold ink">
              <option value="">All items</option>
              {items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </div>

          {loading && <div className="mt-8 ink-subtle text-sm">Loading movements…</div>}

          {!loading && (
            <div className="mt-8 border-y hairline-soft divide-y hairline-soft animate-fade-up animate-fade-up-2" data-testid="inv-mv-list">
              <div className="grid grid-cols-12 py-3 tiny-label ink-subtle">
                <div className="col-span-1"></div>
                <div className="col-span-3">Item</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2 text-right">Qty (base)</div>
                <div className="col-span-2">Reason</div>
                <div className="col-span-2 text-right">At</div>
              </div>
              {movements.length === 0 && (
                <div className="py-8 ink-subtle text-center text-sm">No movements match the filter.</div>
              )}
              {movements.map((m) => (
                <div key={m.id} className="grid grid-cols-12 py-3 items-center" data-testid={`inv-mv-row-${m.id}`}>
                  <div className="col-span-1 text-center">
                    {INCREASES.has(m.type)
                      ? <ArrowDownRight size={14} className="mx-auto ink"/>
                      : <ArrowUpRight size={14} className="mx-auto ink-subtle"/>}
                  </div>
                  <div className="col-span-3 pr-2">
                    <div className="text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{m.item_name}</div>
                    <div className="text-xs ink-subtle mono mt-0.5">{m.item_code}</div>
                  </div>
                  <div className="col-span-2 tiny-label">{TYPE_LABEL[m.type] || m.type}</div>
                  <div className={`col-span-2 text-right mono text-sm ${m.quantity_base >= 0 ? "ink" : "text-red-500"}`}>
                    {m.quantity_base > 0 ? "+" : ""}{m.quantity_base} {m.base_unit}
                  </div>
                  <div className="col-span-2 text-xs ink-subtle italic truncate pr-2">{m.reason || m.notes || "—"}</div>
                  <div className="col-span-2 text-right mono text-[11px] ink-subtle">
                    {new Date(m.at).toLocaleString("en-PH", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Record drawer */}
      {adding && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="inv-mv-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => setAdding(false)}/>
          <form onSubmit={submit} className="relative w-full max-w-md surface-2 border-l hairline h-full p-6 overflow-y-auto shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <div className="section-eyebrow">Inventory · Movement</div>
                <h2 className="mt-2 display-heading text-2xl ink">Record movement</h2>
              </div>
              <button type="button" onClick={() => setAdding(false)} className="tiny-label ink-subtle hover:ink"><X size={16}/></button>
            </div>

            <F label="Type">
              <select required value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="i" data-testid="inv-form-type">
                <option value="receive">Receive</option>
                <option value="wastage">Wastage</option>
                <option value="adjustment">Adjustment (signed)</option>
                <option value="transfer_in">Transfer in</option>
                <option value="transfer_out">Transfer out</option>
                <option value="opening">Opening balance</option>
              </select>
            </F>

            <F label="Item">
              <select required value={form.item_id} onChange={(e) => setForm({ ...form, item_id: e.target.value })} className="i" data-testid="inv-form-item">
                <option value="">— Select item —</option>
                {items.map((i) => <option key={i.id} value={i.id}>{i.name} ({i.base_unit})</option>)}
              </select>
            </F>

            {item && (form.type === "receive" || form.type === "transfer_in" || form.type === "transfer_out") && (
              <F label="Enter quantity in">
                <div className="mt-2 flex gap-2">
                  <button type="button" onClick={() => setForm({ ...form, as_purchase_unit: true })}
                          className={`flex-1 h-10 rounded-md text-[10px] uppercase tracking-[0.24em] font-bold border ${form.as_purchase_unit ? "bg-foreground text-background border-foreground" : "hairline"}`}>
                    {item.purchase_unit}
                  </button>
                  <button type="button" onClick={() => setForm({ ...form, as_purchase_unit: false })}
                          className={`flex-1 h-10 rounded-md text-[10px] uppercase tracking-[0.24em] font-bold border ${!form.as_purchase_unit ? "bg-foreground text-background border-foreground" : "hairline"}`}>
                    {item.base_unit}
                  </button>
                </div>
              </F>
            )}

            <F label={`Quantity ${item ? `(${form.as_purchase_unit && (form.type === "receive" || form.type.startsWith("transfer")) ? item.purchase_unit : item.base_unit})` : ""}`}>
              <input required type="number" step="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                     className="i mono text-lg" data-testid="inv-form-qty"/>
              {form.type === "adjustment" && (
                <div className="mt-1 tiny-label ink-subtle">Use negative numbers to decrease stock.</div>
              )}
            </F>

            {form.type === "receive" && (
              <F label={`Unit cost per ${item?.base_unit || "base unit"} (optional)`}>
                <input type="number" step="0.01" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })}
                       className="i mono" data-testid="inv-form-unit-cost"/>
              </F>
            )}

            {(form.type === "wastage" || form.type === "adjustment") && (
              <F label="Reason (required)">
                <input required value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                       className="i" data-testid="inv-form-reason"
                       placeholder={form.type === "wastage" ? "e.g. spillage, breakage, expired" : "e.g. inventory error"}/>
              </F>
            )}

            <F label="Notes">
              <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="i"/>
            </F>

            <div className="mt-6 flex gap-2">
              <button type="button" onClick={() => setAdding(false)} className="btn-pill btn-pill-outline h-11 flex-1">Cancel</button>
              <button type="submit" data-testid="inv-form-submit-mv" className="btn-pill btn-pill-solid h-11 flex-1"><Scale size={13}/> Record</button>
            </div>

            <style>{`.i { margin-top: 6px; height: 42px; width: 100%; border-radius: 8px; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); padding: 0 14px; color: hsl(var(--foreground)); font-size: 13px; }`}</style>
          </form>
        </div>
      )}
    </div>
  );
}

const BLANK = { item_id: "", type: "receive", quantity: "", as_purchase_unit: true, unit_cost: "", reason: "", notes: "" };

function F({ label, children }) {
  return <label className="block mt-4"><div className="tiny-label">{label}</div>{children}</label>;
}
